import { useCallback, useRef, useEffect } from 'react';
import { GeocodingResult } from 'utils/geocoding';

interface CacheEntry {
    coordinates: GeocodingResult;
    timestamp: number;
}

interface CoordinatesCache {
    [key: string]: CacheEntry;
}

const STORAGE_KEY = 'mapbox_coordinates_cache';
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days
const STORAGE_LIMIT = 500;

export const useCoordinatesCache = () => {
    const cache = useRef<CoordinatesCache>({});

    useEffect(() => {
        try {
            const storedCache = localStorage.getItem(STORAGE_KEY);
            if (storedCache) {
                const parsedCache = JSON.parse(storedCache) as CoordinatesCache;
                const now = Date.now();
                const cleanedCache = Object.entries(parsedCache).reduce((acc, [key, entry]) => {
                    if (now - entry.timestamp < CACHE_EXPIRY) {
                        acc[key] = entry;
                    }
                    return acc;
                }, {} as CoordinatesCache);
                cache.current = cleanedCache;
            }
        } catch (error) {
            console.error('Error loading coordinates cache:', error);
        }
    }, []);

    const saveToLocalStorage = useCallback((cacheData: CoordinatesCache) => {
        try {
            const entries = Object.entries(cacheData);
            if (entries.length > STORAGE_LIMIT) {
                const sortedEntries = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
                const newEntries = sortedEntries.slice(-STORAGE_LIMIT);
                const trimmedCache = Object.fromEntries(newEntries);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedCache));
                return;
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error saving coordinates cache:', error);
        }
    }, []);

    const getCachedCoordinates = useCallback((searchQuery: string): GeocodingResult | null => {
        const key = searchQuery;
        const memoryEntry = cache.current[key];
        if (memoryEntry) {
            if (Date.now() - memoryEntry.timestamp < CACHE_EXPIRY) {
                return memoryEntry.coordinates;
            }
            delete cache.current[key];
            saveToLocalStorage(cache.current);
        }

        return null;
    }, [saveToLocalStorage]);

    const setCachedCoordinates = useCallback((
        searchQuery: string,
        coordinates: GeocodingResult,
    ) => {
        const key = searchQuery;
        const entry = {
            coordinates,
            timestamp: Date.now(),
        };

        cache.current[key] = entry;
        saveToLocalStorage(cache.current);
    }, [saveToLocalStorage]);

    return {
        getCachedCoordinates,
        setCachedCoordinates,
    };
};
